import styled from 'styled-components'

import { theme } from '../../../constants'

const desktop = theme.breakpoints.desktop

const getPlaybackProgressColor = (progress: number) => `color-mix(
  in srgb,
  ${theme.colors.brandYellowSoft} ${100 - progress}%,
  ${theme.colors.brandCoralVivid} ${progress}%
)`

const getPlaybackTrackBackground = (progress: number) => `
  linear-gradient(
    90deg,
    transparent 0%,
    transparent ${progress}%,
    ${theme.colors.borderStrong} ${progress}%,
    ${theme.colors.borderStrong} 100%
  ),
  linear-gradient(
    90deg,
    ${theme.colors.brandYellowSoft} 0%,
    ${theme.colors.brandCoralVivid} 100%
  )
`

export const NowPlayingPanel = styled.div`
  position: sticky;
  top: ${theme.spacing[6]};
  align-self: stretch;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xlarge};
  background: ${theme.colors.surfaceElevated};
  padding: clamp(${theme.spacing[4]}, 3vw, ${theme.spacing[6]});
  box-shadow: ${theme.shadows.medium};
  overflow: hidden;
  box-sizing: border-box;

  .time {
    display: flex;
    justify-content: space-between;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.size.caption};
  }

  &::before {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: ${theme.radii.round};
    top: -105px;
    right: -70px;
    background: ${theme.colors.brandCoralVivid};
    pointer-events: none;
  }

  @media (max-width: ${desktop}) {
    position: relative;
    top: auto;
  }

  @media (max-height: 520px) and (orientation: landscape) {
    padding: ${theme.spacing[4]};
  }
`

export const PlayerHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  align-items: center;

  h3 {
    margin: ${theme.spacing[1]} 0;
    font-size: clamp(22px, 4vw, 32px);
    color: ${theme.colors.textPrimary};
  }

  span {
    color: ${theme.roleColors.student.strong};
    font-weight: ${theme.typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: ${theme.typography.size.caption};
  }

  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
  }

  @media (max-height: 520px) and (orientation: landscape) {
    h3 {
      font-size: 24px;
    }
  }
`

export const ArtworkFrame = styled.div<{ $active?: boolean }>`
  position: relative;
  isolation: isolate;
  width: min(100%, 300px);
  aspect-ratio: 1;
  margin: ${theme.spacing[12]} auto;
  border-radius: ${theme.radii.round};
  overflow: visible;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.large};
  transform: rotate(-1deg);
  transition:
    transform ${theme.motion.slow} ${theme.motion.easing},
    box-shadow ${theme.motion.slow} ${theme.motion.easing};

  @media (max-width: ${desktop}) and (orientation: landscape) {
    width: min(100%, 230px);
    margin: ${theme.spacing[8]} auto;
  }

  @media (max-width: ${desktop}) and (orientation: portrait) {
    width: min(100%, 300px);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: min(100%, 240px);
  }

  @media (max-height: 520px) and (orientation: landscape) {
    width: min(100%, 180px);
    margin: ${theme.spacing[4]} auto;
  }

  ${({ $active }) =>
    $active &&
    `
      transform: rotate(0deg) scale(1.015);
    `}
`

export const ArtworkAura = styled.span<{ $active?: boolean }>`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;

  i {
    position: absolute;
    inset: -12px;
    display: block;
    border-radius: ${theme.radii.round};
    background: color-mix(
      in srgb,
      ${theme.roleColors.student.main} 18%,
      transparent
    );
    opacity: 0;
    transform: scale(0.94);
    transition: opacity ${theme.motion.normal} ${theme.motion.easing};
  }

  i:nth-child(2) {
    inset: -25px;
  }

  i:nth-child(3) {
    inset: -38px;
  }

  ${({ $active }) =>
    $active &&
    `
      i {
        animation: artwork-breathing 2.8s ease-in-out infinite;
      }

      i:nth-child(2) {
        animation-delay: 200ms;
      }

      i:nth-child(3) {
        animation-delay: 500ms;
      }
    `}

  @keyframes artwork-breathing {
    0%,
    100% {
      opacity: 0.08;
      transform: scale(0.85);
    }

    75% {
      opacity: 0.3;
      transform: scale(0.94);
    }

    50% {
      opacity: 0.58;
      transform: scale(1.02);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
    }

    ${({ $active }) =>
      $active &&
      `
        i {
          opacity: 0.18;
          transform: scale(1);
        }

        i:nth-child(2) {
          opacity: 0.12;
        }

        i:nth-child(3) {
          opacity: 0.08;
        }
      `}
  }
`

export const Artwork = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: ${theme.radii.round};
`

export const Progress = styled.input<{ $progress: number }>`
  width: 100%;
  height: 20px;
  margin: 0;
  appearance: none;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 8px;
    border: 1px solid ${theme.colors.borderStrong};
    border-radius: ${theme.radii.pill};
    background: ${({ $progress }) => getPlaybackTrackBackground($progress)};
  }

  &::-webkit-slider-thumb {
    width: 22px;
    height: 22px;
    margin-top: -8px;
    appearance: none;
    border: 2px solid ${theme.colors.surfaceElevated};
    border-radius: ${theme.radii.round};
    background: ${({ $progress }) => getPlaybackProgressColor($progress)};
    box-shadow: ${theme.shadows.small};
  }

  &::-moz-range-track {
    height: 8px;
    border: 1px solid ${theme.colors.borderStrong};
    border-radius: ${theme.radii.pill};
    background: ${({ $progress }) => getPlaybackTrackBackground($progress)};
  }

  &::-moz-range-progress {
    height: 8px;
    border-radius: ${theme.radii.pill};
    background: transparent;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border: 2px solid ${theme.colors.surfaceElevated};
    border-radius: ${theme.radii.round};
    background: ${({ $progress }) => getPlaybackProgressColor($progress)};
    box-shadow: ${theme.shadows.small};
  }

  &:focus-visible {
    outline: 3px solid ${theme.semanticColors.focus};
    outline-offset: 2px;
    border-radius: ${theme.radii.pill};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.62;
  }
`

export const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[6]};
  margin: ${theme.spacing[4]} 0 0;

  @media (max-width: ${desktop}) and (orientation: landscape) {
    gap: ${theme.spacing[4]};
  }
`

export const ControlButton = styled.button<{ $primary?: boolean }>`
  width: ${({ $primary }) => ($primary ? '56px' : '44px')};
  height: ${({ $primary }) => ($primary ? '56px' : '44px')};
  border-radius: 50%;
  border: 1px solid ${theme.colors.border};
  display: grid;
  place-items: center;
  cursor: pointer;
  color: ${({ $primary }) =>
    $primary ? theme.roleColors.student.text : theme.colors.textPrimary};
  background: ${({ $primary }) =>
    $primary ? theme.roleColors.student.main : theme.colors.surfaceElevated};
  font-size: ${({ $primary }) => ($primary ? '20px' : '14px')};

  > svg {
    width: ${({ $primary }) => ($primary ? '20px' : '15px')};
    height: ${({ $primary }) => ($primary ? '20px' : '15px')};
    display: block;
    color: currentColor;
    fill: currentColor;
    stroke: currentColor;
  }

  &:focus-visible {
    outline: 3px solid ${theme.semanticColors.focus};
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.small};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.62;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: ${desktop}) and (orientation: landscape) {
    width: ${({ $primary }) => ($primary ? '52px' : '44px')};
    height: ${({ $primary }) => ($primary ? '52px' : '44px')};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
